import { Button, TableItem, TableMessage } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiPlus, PiTrash } from 'react-icons/pi';
import { AsapType } from 'services/digital-delivery/types';
import { SUBSCRIPTION_TYPE } from 'services/music-product-digital/types/enums';
import {
  IDeliveryTemplateItem,
  ISetDeliveryTemplateRequest,
} from 'services/settings/digital-delivery-template/types';
import { useCountries } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { II18nextPrefix } from 'types/components/components.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { array, boolean, number, object, string } from 'yup';
import { IFormikProps, IFormProps } from '../types';

export const digitalDeliveryTemplateInitialValues: ISetDeliveryTemplateRequest =
  {
    included: [],
    excluded: [],
  };

const itemValidator = object().shape({
  countryCode: string().required(i18next.t('formik-error.required')),
  subscriptionTypeId: number().required(i18next.t('formik-error.required')),
  asap: number().required(i18next.t('formik-error.required')),
  isTimeZoneBased: boolean().required(i18next.t('formik-error.required')),
});

export const digitalDeliveryTemplateValidationSchema = object().shape({
  included: array().of(itemValidator).nullable(),
  excluded: array().of(itemValidator).nullable(),
});

export interface DigitalDeliveryTemplateFormProps
  extends
    FormikConfig<ISetDeliveryTemplateRequest>,
    IFormProps,
    Partial<II18nextPrefix> {}

const DigitalDeliveryTemplateForm: React.FC<
  DigitalDeliveryTemplateFormProps
> = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  status,
  initialValues = digitalDeliveryTemplateInitialValues,
  onSubmit,
  backPath,
  customBackItem,
  ...props
}) => {
  const { t } = useTranslation();
  const { getSelectOptions } = useEnumToSelectOptions();
  const countries = useCountries();

  const countryOptions = useMemo(() => {
    if (!countries || countries.length === 0) return [];
    return [
      {
        label: t('music-product-digital.delivery.all'),
        value: 'ALL',
      },
      ...countries.map(country => ({
        label: t(`countries.${country.codeAlfa2}`),
        value: country.codeAlfa2,
      })),
    ];
  }, [countries, t]);

  const addItem = (
    type: 'included' | 'excluded',
    setFieldValue: any,
    values: ISetDeliveryTemplateRequest,
  ) => {
    if (!countries || countries.length === 0) return;

    const newItem: IDeliveryTemplateItem = {
      countryCode: '',
      subscriptionTypeId: SUBSCRIPTION_TYPE.all,
      asap: AsapType.None,
      isTimeZoneBased: false,
    };
    setFieldValue(`${type}[${values[type]?.length ?? 0}]`, newItem);
  };

  const removeItem = (
    type: 'included' | 'excluded',
    index: number,
    setFieldValue: any,
    values: ISetDeliveryTemplateRequest,
  ) => {
    setFieldValue(
      type,
      values[type].filter((_, i) => i !== index),
    );
  };

  return (
    <Formik
      {...props}
      initialStatus={status}
      validateOnChange={true}
      validateOnBlur={true}
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={digitalDeliveryTemplateValidationSchema}
      onSubmit={onSubmit}>
      {({
        values,
        setFieldValue,
        handleSubmit,
      }: IFormikProps<ISetDeliveryTemplateRequest>) => (
        <TopbarPage
          topbar={
            <TopBar
              title={tittle || t('setting.digital-delivery-template.title')}
              topbarContent={topbarContent}
              path={backPath}
              customBackItem={customBackItem}
              hideBackButton={status?.displayMode}
            />
          }>
          <Form className='w-full h-full' onSubmit={handleSubmit}>
            <div className='flex flex-col-reverse h-full w-full gap-4 justify-end mb-[200px]'>
              <div className='w-full h-max'>
                <Table
                  id={'DigitalDeliveryTemplateExcluded'}
                  size='compact'
                  overflowHidden={false}
                  data={{
                    nodes:
                      values.excluded?.map((item, index) => ({
                        ...item,
                        id: index,
                      })) ?? [],
                  }}
                  columns={[
                    {
                      label: t('setting.digital-delivery-template.excluded'),
                      renderCell: ({ id }) => {
                        const item = values.excluded[id];
                        const selectedCountry = countryOptions.find(
                          opt => opt.value === item?.countryCode,
                        );

                        return (
                          <TableItem>
                            <FormInput.Select
                              displayMode={status?.displayMode ?? false}
                              name={`excluded[${id}].countryCode`}
                              label={t('input.label.country')}
                              options={countryOptions}
                              value={
                                selectedCountry
                                  ? {
                                      value: selectedCountry.value,
                                      label: selectedCountry.label,
                                    }
                                  : undefined
                              }
                              onChange={value => {
                                setFieldValue(
                                  `excluded[${id}].countryCode`,
                                  value.value,
                                );
                              }}
                            />
                          </TableItem>
                        );
                      },
                      resize: true,
                    },
                    {
                      label: t('input.label.subscriptionType'),
                      renderCell: ({ id }) => {
                        const item = values.excluded[id];
                        const subscriptionOptions = getSelectOptions(
                          SUBSCRIPTION_TYPE,
                          'enum.SUBSCRIPTION_TYPE',
                        );
                        const selectedSubscription = subscriptionOptions.find(
                          opt => opt.value === item?.subscriptionTypeId,
                        );

                        return (
                          <TableItem>
                            <FormInput.Select
                              displayMode={status?.displayMode ?? false}
                              name={`excluded[${id}].subscriptionTypeId`}
                              label={t('input.label.subscriptionType')}
                              options={subscriptionOptions}
                              value={
                                selectedSubscription
                                  ? {
                                      value: selectedSubscription.value,
                                      label: selectedSubscription.label,
                                    }
                                  : undefined
                              }
                              onChange={value => {
                                setFieldValue(
                                  `excluded[${id}].subscriptionTypeId`,
                                  value.value,
                                );
                              }}
                            />
                          </TableItem>
                        );
                      },
                      resize: true,
                    },
                    {
                      label: t('input.label.asap'),
                      renderCell: ({ id }) => {
                        const item = values.excluded[id];
                        const asapOptions = getSelectOptions(
                          AsapType,
                          'enum.AsapType',
                        );
                        const selectedAsap = asapOptions.find(
                          opt => opt.value === item?.asap,
                        );

                        return (
                          <TableItem>
                            <FormInput.Select
                              displayMode={status?.displayMode ?? false}
                              name={`excluded[${id}].asap`}
                              label={t('input.label.asap')}
                              options={asapOptions}
                              value={
                                selectedAsap
                                  ? {
                                      value: selectedAsap.value,
                                      label: selectedAsap.label,
                                    }
                                  : undefined
                              }
                              onChange={value => {
                                setFieldValue(
                                  `excluded[${id}].asap`,
                                  value.value,
                                );
                              }}
                            />
                          </TableItem>
                        );
                      },
                      resize: true,
                    },
                    {
                      label: t('input.label.timeZoneBased'),
                      renderCell: ({ id }) => {
                        const item = values.excluded[id];
                        const timezoneOptions = [
                          { label: t('input.option.yes'), value: true },
                          { label: t('input.option.no'), value: false },
                        ];
                        const selectedTimezone = timezoneOptions.find(
                          opt => opt.value === item?.isTimeZoneBased,
                        );

                        return (
                          <TableItem>
                            <FormInput.Select
                              displayMode={status?.displayMode ?? false}
                              name={`excluded[${id}].isTimeZoneBased`}
                              label={t('input.label.timeZoneBased')}
                              options={timezoneOptions}
                              value={
                                selectedTimezone
                                  ? {
                                      value: selectedTimezone.value,
                                      label: selectedTimezone.label,
                                    }
                                  : undefined
                              }
                              onChange={value => {
                                setFieldValue(
                                  `excluded[${id}].isTimeZoneBased`,
                                  value.value,
                                );
                              }}
                            />
                          </TableItem>
                        );
                      },
                      resize: true,
                    },
                    {
                      label: '',
                      renderCell: ({ id }) =>
                        !status?.displayMode ? (
                          <TableItem justifyPosition='end'>
                            <Button
                              type='button'
                              variant={BUTTON_VARIANT.remove}
                              onlyIcon
                              onClick={() =>
                                removeItem(
                                  'excluded',
                                  id,
                                  setFieldValue,
                                  values,
                                )
                              }
                              isClear>
                                                            <Button.Description description={t('button.remove')} />

                                                            <Button.Icon icon={<PiTrash />} />
                            </Button>
                          </TableItem>
                        ) : null,
                    },
                  ]}
                  tableOptions={
                    status?.displayMode
                      ? undefined
                      : {
                          renderAfterTable: () =>
                            countries && countries.length > 0 ? (
                              <tfoot className='full-width-footer'>
                                <TableMessage
                                  className='cursor-pointer'
                                  onClick={() =>
                                    addItem('excluded', setFieldValue, values)
                                  }>
                                  <TableItem.Text text={t('button.add')} />
                                  <PiPlus />
                                </TableMessage>
                              </tfoot>
                            ) : null,
                        }
                  }
                />
              </div>

              <div className='w-full h-max'>
                <Table
                  id={'DigitalDeliveryTemplateIncluded'}
                  size='compact'
                  overflowHidden={false}
                  data={{
                    nodes:
                      values.included?.map((item, index) => ({
                        ...item,
                        id: index,
                      })) ?? [],
                  }}
                  columns={[
                    {
                      label: t('setting.digital-delivery-template.included'),
                      renderCell: ({ id }) => {
                        const item = values.included[id];
                        const selectedCountry = countryOptions.find(
                          opt => opt.value === item?.countryCode,
                        );

                        return (
                          <TableItem>
                            <FormInput.Select
                              displayMode={status?.displayMode ?? false}
                              name={`included[${id}].countryCode`}
                              label={t('input.label.country')}
                              options={countryOptions}
                              value={
                                selectedCountry
                                  ? {
                                      value: selectedCountry.value,
                                      label: selectedCountry.label,
                                    }
                                  : undefined
                              }
                              onChange={value => {
                                setFieldValue(
                                  `included[${id}].countryCode`,
                                  value.value,
                                );
                              }}
                            />
                          </TableItem>
                        );
                      },
                      resize: true,
                    },
                    {
                      label: t('input.label.subscriptionType'),
                      renderCell: ({ id }) => {
                        const item = values.included[id];
                        const subscriptionOptions = getSelectOptions(
                          SUBSCRIPTION_TYPE,
                          'enum.SUBSCRIPTION_TYPE',
                        );
                        const selectedSubscription = subscriptionOptions.find(
                          opt => opt.value === item?.subscriptionTypeId,
                        );

                        return (
                          <TableItem>
                            <FormInput.Select
                              displayMode={status?.displayMode ?? false}
                              name={`included[${id}].subscriptionTypeId`}
                              label={t('input.label.subscriptionType')}
                              options={subscriptionOptions}
                              value={
                                selectedSubscription
                                  ? {
                                      value: selectedSubscription.value,
                                      label: selectedSubscription.label,
                                    }
                                  : undefined
                              }
                              onChange={value => {
                                setFieldValue(
                                  `included[${id}].subscriptionTypeId`,
                                  value.value,
                                );
                              }}
                            />
                          </TableItem>
                        );
                      },
                      resize: true,
                    },
                    {
                      label: t('input.label.asap'),
                      renderCell: ({ id }) => {
                        const item = values.included[id];
                        const asapOptions = getSelectOptions(
                          AsapType,
                          'enum.AsapType',
                        );
                        const selectedAsap = asapOptions.find(
                          opt => opt.value === item?.asap,
                        );

                        return (
                          <TableItem>
                            <FormInput.Select
                              displayMode={status?.displayMode ?? false}
                              name={`included[${id}].asap`}
                              label={t('input.label.asap')}
                              options={asapOptions}
                              value={
                                selectedAsap
                                  ? {
                                      value: selectedAsap.value,
                                      label: selectedAsap.label,
                                    }
                                  : undefined
                              }
                              onChange={value => {
                                setFieldValue(
                                  `included[${id}].asap`,
                                  value.value,
                                );
                              }}
                            />
                          </TableItem>
                        );
                      },
                      resize: true,
                    },
                    {
                      label: t('input.label.timeZoneBased'),
                      renderCell: ({ id }) => {
                        const item = values.included[id];
                        const timezoneOptions = [
                          { label: t('input.option.yes'), value: true },
                          { label: t('input.option.no'), value: false },
                        ];
                        const selectedTimezone = timezoneOptions.find(
                          opt => opt.value === item?.isTimeZoneBased,
                        );

                        return (
                          <TableItem>
                            <FormInput.Select
                              displayMode={status?.displayMode ?? false}
                              name={`included[${id}].isTimeZoneBased`}
                              label={t('input.label.timeZoneBased')}
                              options={timezoneOptions}
                              value={
                                selectedTimezone
                                  ? {
                                      value: selectedTimezone.value,
                                      label: selectedTimezone.label,
                                    }
                                  : undefined
                              }
                              onChange={value => {
                                setFieldValue(
                                  `included[${id}].isTimeZoneBased`,
                                  value.value,
                                );
                              }}
                            />
                          </TableItem>
                        );
                      },
                      resize: true,
                    },
                    {
                      label: '',
                      renderCell: ({ id }) =>
                        !status?.displayMode ? (
                          <TableItem justifyPosition='end'>
                            <Button
                              type='button'
                              variant={BUTTON_VARIANT.remove}
                              onlyIcon
                              onClick={() =>
                                removeItem(
                                  'included',
                                  id,
                                  setFieldValue,
                                  values,
                                )
                              }
                              isClear>
                                                            <Button.Description description={t('button.remove')} />

                                                            <Button.Icon icon={<PiTrash />} />
                            </Button>
                          </TableItem>
                        ) : null,
                    },
                  ]}
                  tableOptions={
                    status?.displayMode
                      ? undefined
                      : {
                          renderAfterTable: () =>
                            countries && countries.length > 0 ? (
                              <tfoot className='full-width-footer'>
                                <TableMessage
                                  className='cursor-pointer'
                                  onClick={() =>
                                    addItem('included', setFieldValue, values)
                                  }>
                                  <TableItem.Text text={t('button.add')} />
                                  <PiPlus />
                                </TableMessage>
                              </tfoot>
                            ) : null,
                        }
                  }
                />
              </div>
            </div>
          </Form>
        </TopbarPage>
      )}
    </Formik>
  );
};

export default DigitalDeliveryTemplateForm;
